// `/intelligence?from=/some/page` context handling.
//
// The client may propose a page it came from. That value is UNTRUSTED: it is a
// URL parameter anyone can craft. Only a same-site, public, canonical path is
// ever forwarded to the server, and no DOM content is ever ingested.

/** Route prefixes that are private, authenticated, admin, or non-content. */
export const PRIVATE_PATH_PREFIXES = [
  "/admin",
  "/auth",
  "/login",
  "/signup",
  "/account",
  "/settings",
  "/dashboard",
  "/me",
  "/checkout",
  "/orders",
  "/library/private",
  "/api",
  "/debug",
  "/_",
  "/intelligence",
] as const;

const ALLOWED_CHARS = /^[A-Za-z0-9/_\-.~]+$/;

/**
 * Validate and normalise a proposed public path.
 * Returns null for anything external, private, encoded-abusive, or malformed.
 */
export function sanitizePublicPath(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  let value = raw.trim();
  if (!value || value.length > 200) return null;

  // Reject absolute URLs, protocol-relative URLs and scheme-ish values outright.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  if (value.startsWith("//")) return null;
  if (!value.startsWith("/")) return null;

  // One controlled decode pass; reject anything that fails or re-introduces
  // structural characters (query, fragment, backslash, encoded traversal).
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }
  if (decoded !== value && /^[a-z][a-z0-9+.-]*:/i.test(decoded.trim())) return null;
  value = decoded;

  if (/[?#\\\s]/.test(value)) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;
  if (value.includes("..")) return null;
  if (value.startsWith("//")) return null;
  if (!ALLOWED_CHARS.test(value)) return null;

  // Normalise trailing slash (except root).
  if (value.length > 1 && value.endsWith("/")) value = value.slice(0, -1);

  const lower = value.toLowerCase();
  for (const prefix of PRIVATE_PATH_PREFIXES) {
    if (lower === prefix || lower.startsWith(`${prefix}/`)) return null;
  }
  return value;
}

/** Public prefixes we can confidently describe in the context chip. */
const KNOWN_SECTIONS: { prefix: string; label: string }[] = [
  { prefix: "/music", label: "Music" },
  { prefix: "/books", label: "Books" },
  { prefix: "/codex", label: "Codex" },
  { prefix: "/gates", label: "The 47 Gates" },
  { prefix: "/activations", label: "Activations" },
  { prefix: "/wisdom", label: "Wisdom" },
  { prefix: "/beats", label: "Beats" },
  { prefix: "/academy", label: "Academy" },
  { prefix: "/artist", label: "Artist" },
  { prefix: "/press", label: "Press" },
  { prefix: "/news", label: "Newsroom" },
  { prefix: "/about", label: "About" },
  { prefix: "/shop", label: "Shop" },
];

/** A short, human label for the context chip. Never echoes arbitrary DOM text. */
export function describePublicPath(path: string): string {
  if (path === "/") return "Home";
  const section = KNOWN_SECTIONS.find((s) => path === s.prefix || path.startsWith(`${s.prefix}/`));
  const last = path.split("/").filter(Boolean).pop() ?? "";
  const pretty = last.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  if (!section) return pretty || path;
  return path === section.prefix ? section.label : `${section.label} · ${pretty}`;
}

/** Read and sanitise the `from` parameter of a search string. */
export function publicPathFromSearch(search: string | null | undefined): string | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    return sanitizePublicPath(params.get("from"));
  } catch {
    return null;
  }
}
